import React, { useEffect, useState } from 'react';
import moment from 'moment';
import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import type { TagType } from '@amzn/meridian/tag/tag';
import MeridianLoader from '@amzn/meridian/loader';
import Tooltip from '@amzn/meridian/tooltip';
import { useTranslation } from 'src/arb-wrapper';
import { useWorkflowsControllerFindManyQuery } from 'src/store/leela-api';
import { type WorkflowDto } from 'src/store/generated/leela-api-generated';
import { WorkflowStatuses } from 'src/store/constants';
import { Loader } from 'src/components/common/Loader';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { NoResultsTableRow } from 'src/components/common/data-table-action-bar/NoResultsTableRow';
import { TablePagination } from 'src/components/common/data-table-action-bar/TablePagination';
import { EMPTY_TABLE_VALUE } from 'src/constants';
import Icon from '@amzn/meridian/icon';
import { clockIcon } from 'src/store/icons';

interface ProgramWorkflowsProps {
  programId: string;
  campaignId?: string;
}

/**
 * Get the count of unique asset IDs from workflow products
 */
const getUniqueAssetCount = (products?: WorkflowDto['productsAdded']): number => {
  if (!Array.isArray(products)) return 0;
  const uniqueAssetIds = new Set(products.map(product => product.assetId));
  return uniqueAssetIds.size;
};

/**
 * Get the count of unique product set IDs from workflow products
 */
const getUniqueProductSetCount = (products?: WorkflowDto['productsAdded']): number => {
  if (!Array.isArray(products)) return 0;
  const uniqueProductSetIds = new Set(products.map(p => p.productSetId).filter(Boolean));
  return uniqueProductSetIds.size;
};

/**
 * Displays the unique asset count. When there are multiple unique product set IDs,
 * shows the product set count in parentheses with a tooltip explaining it's the number of variants.
 */
const WorkflowProductCount: React.FC<{
  products?: WorkflowDto['productsAdded'];
  workflowId: string;
  type: 'added' | 'removed';
}> = ({ products, workflowId, type }) => {
  const uniqueCount = getUniqueAssetCount(products);
  const totalCount = Array.isArray(products) ? products.length : 0;
  const productSetCount = getUniqueProductSetCount(products);
  const hasVariants = totalCount > uniqueCount;

  if (uniqueCount === 0) return <>{EMPTY_TABLE_VALUE}</>;

  return (
    <Row alignmentVertical="center" spacing="100">
      <span>{uniqueCount}</span>
      {hasVariants && (
        <Tooltip
          id={`${type}-variants-${workflowId}`}
          position="end"
          title={`${productSetCount} unique product set variants`}
        >
          <span style={{ color: '#879596', cursor: 'default' }}>
            ({totalCount})
          </span>
        </Tooltip>
      )}
    </Row>
  );
};

export const ProgramWorkflows: React.FC<ProgramWorkflowsProps> = ({ programId, campaignId }: ProgramWorkflowsProps) => {
  const { t } = useTranslation();
  const { createToast } = useToasterContext();
  const [workflows, setWorkflows] = useState<WorkflowDto[]>([]);

  const itemsPerPageOptions = [20, 50, 100];

  const [pagination, setPagination] = useState({
    page: 1,
    pageCount: 1,
    pageSize: 20,
  });

  const { data, error, isLoading } = useWorkflowsControllerFindManyQuery({
    programId,
    campaignId,
    pageSize: pagination.pageSize,
    page: pagination.page,
  }, { pollingInterval: 10000 });

  useEffect(() => {
    setWorkflows(data?.data ?? []);
    setPagination(p => ({
      ...p,
      pageCount: data?.meta?.pagination?.pageCount ?? 1,
    }));
  }, [data]);

  useEffect(() => {
    if (error) {
      const {
        data: { error: errorMessage },
      } = error as { data: { error: string } };
      createToast({
        type: AlertType.ERROR,
        message: `${t('form_workflows_error_fetch')}: ${errorMessage}`,
      });
    }
  }, [error, t, createToast]);

  const getStatusTagType = (status: string): TagType => {
    switch (status) {
      case WorkflowStatuses.QUEUED:
        return 'warning';
      case WorkflowStatuses.IN_PROGRESS:
        return 'warning';
      case WorkflowStatuses.COMPLETED:
        return 'success';
      case WorkflowStatuses.DELETED:
        return 'neutral';
      default:
        return 'error';
    }
  };

  return (
    <Loader isLoading={isLoading}>
      <Column>
        <Table
          data-testid="workflows-table"
          headerRows={1}
          rowComponents={[TableRow, NoResultsTableRow]}
          showDividers={true}
        >
          <TableRow key="workflows-header">
            <TableCell></TableCell>
            <TableCell>{t('form_global_id')}</TableCell>
            <TableCell>{t('form_global_status')}</TableCell>
            <TableCell>{t('form_global_campaign')}</TableCell>
            <TableCell>{t('form_global_created_at')}</TableCell>
            <TableCell>{t('form_global_assets_removed')}</TableCell>
            <TableCell>{t('form_global_assets_introduced')}</TableCell>
          </TableRow>

          {workflows?.length === 0 && <NoResultsTableRow data-testid="no-results-row" span={7} />}
          {workflows.map(workflow => (
            <TableRow data-testid= {`workflow-row-${workflow.workflowId}`} key={workflow.workflowId}>
              <TableCell>
                {{
                  [WorkflowStatuses.QUEUED]: <Icon data-testid = "queued-clock-icon" tokens={clockIcon} />,
                  [WorkflowStatuses.IN_PROGRESS]: <MeridianLoader data-testid = "in-progress-loader" size="small" />,
                }[workflow.status] ?? null}
              </TableCell>
              <TableCell>{workflow.workflowId}</TableCell>
              <TableCell>
                <Row>
                  <Tag type={getStatusTagType(workflow.status)}>{workflow.status}</Tag>
                </Row>
              </TableCell>
              <TableCell>{workflow.campaignId}</TableCell>
              <TableCell>
                <div title={workflow.startTime}>
                  {moment(workflow.startTime).fromNow()}
                </div>
              </TableCell>
              <TableCell>
                <WorkflowProductCount products={workflow.productsRemoved} type="removed" workflowId={workflow.workflowId} />
              </TableCell>
              <TableCell>
                <WorkflowProductCount products={workflow.productsAdded} type="added" workflowId={workflow.workflowId} />
              </TableCell>
            </TableRow>
          ))}
        </Table>

        <TablePagination
          currentPage={pagination.page}
          itemsPerPage={pagination.pageSize}
          itemsPerPageOptions={itemsPerPageOptions}
          pageCount={pagination.pageCount}
          onItemsPerPageChange={(pageSize: number) => {
            setPagination(prev => ({
              ...prev,
              page: 1,
              pageSize,
            }));
          }}
          onPageChange={(newPage: number) => {
            setPagination(prev => ({
              ...prev,
              page: newPage,
            }));
          }}
        />
      </Column>
    </Loader>
  );
};
